import React, {useEffect, useState} from 'react';
import {useRouter} from 'next/router';

import ResumeCard from '../../components/ResumeExplore/ResumeCard';
import Link from '../../components/Link';
import {useAnalytics} from '../../hooks';
import {getLibraryResumes} from '../../api/api';

const COUNT = 3;
// Fewer resumes than this is not worth a strip.
const MIN_RESUMES = 3;

const fetchSegment = ({companyTag, roleTag}) => getLibraryResumes({
  limit: COUNT,
  ...(companyTag && {companies: companyTag}),
  ...(roleTag && {roles: roleTag}),
}).catch(() => null);

/**
 * Three resumes from the library on the main guide tab (Phase 5 preview
 * strip), linking to the resumes tab. Company guides only: the guide's
 * own role when it has enough resumes at the company, else the company's.
 */
export default function GuideResumesPreview({guide, companyName}) {
  const router = useRouter();
  const {tracker} = useAnalytics();
  const [segment, setSegment] = useState(null);
  const companyTag = guide.companyTag || null;
  const roleTag = guide.roleTag || null;

  useEffect(() => {
    if (!companyTag) return undefined;
    let cancelled = false;
    (async () => {
      const own = roleTag ? await fetchSegment({companyTag, roleTag}) : null;
      const chosen = own?.total >= MIN_RESUMES
        ? {...own, role: guide.roleName}
        : await fetchSegment({companyTag});
      if (cancelled || !(chosen?.total >= MIN_RESUMES)) return;
      setSegment(chosen);
      tracker.guideViewResumesPreview({guide: guide.name, resumeCount: chosen.total});
    })();
    return () => { cancelled = true; };
  }, [companyTag, roleTag]);

  if (!segment) return null;
  const tabHref = `/guides/${guide.slug}/resumes`;
  const screen = [`${companyName || companyTag}'s`, segment.role, 'screen']
    .filter(Boolean).join(' ');
  const goToTab = (cta, extra = {}) => {
    tracker.guideClickResumesPreview({guide: guide.name, cta, ...extra});
    tracker.guideClickTab({tab: 'resumes', guide: guide.name, source: 'preview_strip'});
    router.push(tabHref);
  };

  return (
    <section className="mt-10 not-prose rounded-xl border border-gray-200 bg-gray-50 p-5">
      <h2 className="text-lg font-bold text-gray-900 m-0">
        {`${segment.total.toLocaleString('en-US')} resumes that passed ${screen}`}
      </h2>
      <p className="text-sm text-gray-600 mt-1 mb-4">
        Real resumes shared by candidates who got the interview, with the schools,
        companies and skills they list.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {segment.results.slice(0, COUNT).map((resume, index) => (
          // Phones show two cards so the strip stays short.
          <div key={resume.resumeId} className={index === COUNT - 1 ? 'hidden sm:block' : undefined}>
            <ResumeCard
              resume={resume}
              position={index}
              onViewResume={(username, {resumeId, position}) => goToTab('card', {resumeId, position})}
            />
          </div>
        ))}
      </div>
      <p className="mt-4 mb-0 text-sm">
        <Link
          href={tabHref}
          onClick={() => {
            tracker.guideClickResumesPreview({guide: guide.name, cta: 'see_all'});
            tracker.guideClickTab({tab: 'resumes', guide: guide.name, source: 'preview_strip'});
          }}
          className="font-medium text-indigo-600 no-underline hover:text-indigo-700"
        >
          {`See all ${segment.total.toLocaleString('en-US')} resumes ->`}
        </Link>
      </p>
    </section>
  );
}
